import { useState, useEffect } from 'react';
import { Drawer, Button, Form, Typography, Tag, Row, Col, Avatar, Table } from 'antd';
import { PersonFill, ChevronRight, CaretUp, CaretDown } from 'react-bootstrap-icons';
import './index.less';
import config from '@config';
import { dataExpertsApi, dataCaseProjectsApi, dataOrgMemberCountApi } from '@/services/DC/Organization';
import { prepareBCOSSUrl } from '@/utils/utils';
import { orgLeaders } from '@utils/constants';
import { useNavigate } from 'react-router-dom';
import { selectionActions } from '@/components/Selection/store/selectionSlice';
import { expertActions } from '@/components/Expert/overview/store/expertSlice';
import { useDispatch } from 'react-redux';

const { Text, Link } = Typography;

const { setCaseFilter, setCaseCursor } = selectionActions; // 实践列表筛选
const { setFilter: setExpertFilter, setCursor: setExpertCursor } = expertActions; // 专家库列表筛选

// 数据概览
const DataOverviewDrawer = props => {
    const { openDataOverviewDrawer, setOpenDataOverviewDrawer, id, currentOrg } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [info, setInfo] = useState({});
    const [tableData, setTableData] = useState([]);
    const [showAllCurrentLeaders, setShowAllCurrentLeaders] = useState(false); // 现任领导是否展开
    const [showAllFormerLeaders, setShowAllFormerLeaders] = useState(false); // 历任领导是否展开
    const [authCountInfo, setAuthCountInfo] = useState({}); // 认证人数
    const [currentLeaders, setCurrentLeaders] = useState([]); // 现任专家列表
    const [formerLeaders, setFormerLeaders] = useState([]); // 历任专家列表
    const [academicianList, setAcademicianList] = useState([]); // 两院院士
    const [expertsCount, setExpertsCount] = useState(); // 专家总数

    const columns = [
        {
            title: '评议项目名称',
            dataIndex: 'name',
            render: (_, row) => (
                <>
                    {row.name}
                    <Text type="secondary">{'YPX' + ('000000' + row.id).slice(-6)}</Text>
                </>
            ),
        },
        {
            title: '申报实践数',
            dataIndex: 'orgCaseTotal',
            width: 100,
            align: 'center',
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 100,
            align: 'center',
            render: (_, row) => (
                <Button size="small" type="link" onClick={() => handleViewCases(row)}>
                    查看
                </Button>
            ),
        },
    ];

    // 查看 - 更新检索参数 - 跳转实践列表
    const handleViewCases = row => {
        dispatch(
            setCaseFilter({
                projectId: row.id, // 项目id
                hospitalName: currentOrg?.name, // 申报机构名称
                reviewStatus: 1, // 审核状态：0-待审核，1-审核通过，2-审核驳回
                page: 1,
                pageSize: 20,
            }),
        );
        dispatch(setCaseCursor(0));
        navigate('/selection/cases');
    };

    // 查看专家库人员 - 更新检索参数 - 跳转专家库列表
    const viewExperts = () => {
        dispatch(
            setExpertFilter({
                unitName: currentOrg?.name,
                page: 1,
                size: 20,
            }),
        );
        dispatch(setExpertCursor(0));
        navigate('/dc/experts');
    };

    const onClose = () => {
        setOpenDataOverviewDrawer(false);
    };

    // 准备logo url，兼容用户中心上传的 upload 打头的 logo url
    const prepareLogoUrl = logo => {
        let url = '';

        if (!!logo && typeof logo === 'string') {
            if (logo.startsWith('upload/')) {
                url = `${config.FILE_PREFIX}/${logo}`;
            } else {
                url = prepareBCOSSUrl(logo);
            }
        }

        return url;
    };

    //查询机构案例项目列表
    const dataCaseProjects = async () => {
        const res = await dataCaseProjectsApi({ orgId: id });
        if (res && res.status_code === 200 && res.data) {
            setTableData(res.data);
        }
    };

    //查询机构案例认证信息
    const dataOrgMemberCount = async () => {
        const res = await dataOrgMemberCountApi({ orgId: id });
        if (res && res.status_code === 200 && res.data) {
            setAuthCountInfo(res.data);
        }
    };

    // 机构认证类型
    const getAuthType = info => {
        if (!info) {
            return <Text type="secondary">&ndash;</Text>;
        }
        // 机构认证 - 已通过/审核中
        const orgType = info.memType === 2 && (info.memAuthStatus === 2 || info.memAuthStatus === 1);
        if (orgType && parseInt(info.orgType) === 0) {
            return '认证企业';
        }
        if (orgType && parseInt(info.orgType) === 1) {
            return '认证媒体';
        }
        if (orgType && parseInt(info.orgType) === 2) {
            return '认证医疗机构';
        }
        if (orgType && parseInt(info.orgType) === 3) {
            return '认证机构';
        }
        return <Text type="secondary">&ndash;</Text>;
    };

    // 获取领导班子相关职务（按 orgLeaders.sort 排序）
    const getLeaderPositions = (experiences = []) => {
        const roles = (experiences || []).filter(exp => {
            const code = exp && exp.memPostCode;
            return !!orgLeaders.find(l => l.code === code);
        });
        const sorted = roles.sort((a, b) => {
            const leftSort = orgLeaders.find(l => l.code === a.memPostCode)?.sort || 999;
            const rightSort = orgLeaders.find(l => l.code === b.memPostCode)?.sort || 999;
            return leftSort - rightSort;
        });
        return sorted;
    };

    // 获取专家在领导班子的职务文案，按排序拼接“、”
    const getLeaderTitle = item => {
        const sorted = getLeaderPositions(item?.ssoMemberWorkExperiencesEntities || []);
        if (!sorted.length) return '';
        const titles = sorted.map(it => it.unitPost || orgLeaders.find(l => l.code === it.memPostCode)?.name).filter(Boolean);
        const uniqueTitles = Array.from(new Set(titles));
        return uniqueTitles.join('、');
    };

    // 获取专家用于排序的最高职务（最小 sort 值）
    const getLeaderOrder = item => {
        const sorted = getLeaderPositions(item?.ssoMemberWorkExperiencesEntities || []);
        const top = sorted[0];
        if (!top) return 999;
        return orgLeaders.find(l => l.code === top.memPostCode)?.sort || 999;
    };

    //查询机构 - 专家列表
    const dataExperts = async () => {
        const res = await dataExpertsApi({ orgId: id });
        if (res && res.status_code === 200 && res.data) {
            const exList = res.data.expertList || [];
            const formerExList = res.data.formerExpertList || [];
            setExpertsCount(exList.length);

            // 过滤 - 中国科学院院士、中国工程院院士
            const honoraryList = exList.filter(item => item.honoraryTitles && (item.honoraryTitles.split(',').includes('中国科学院院士') || item.honoraryTitles.split(',').includes('中国工程院院士')));
            const resHonoraryList = honoraryList.map(item => {
                const newItem = { ...item };
                const honoraryArr = item.honoraryTitles.split(',');
                if (honoraryArr.includes('中国科学院院士')) {
                    newItem.honoraryTitles = '中国科学院院士';
                }
                if (honoraryArr.includes('中国工程院院士')) {
                    newItem.honoraryTitles = '中国工程院院士';
                }
                return newItem;
            });

            setAcademicianList(resHonoraryList || []);

            // 现任领导 - 只展示领导班子（支持多职务）
            const leaderList = exList.filter(item => {
                return getLeaderPositions(item?.ssoMemberWorkExperiencesEntities || []).length > 0;
            });

            // 根据 orgLeaders.sort 对专家按其最高职务排序
            const orderedExpertList = leaderList.sort((a, b) => {
                return getLeaderOrder(a) - getLeaderOrder(b);
            });

            setCurrentLeaders(orderedExpertList);

            // 历任领导 - 只展示领导班子（支持多职务）
            const leaderFormerList = formerExList.filter(item => {
                return getLeaderPositions(item?.ssoMemberWorkExperiencesEntities || []).length > 0;
            });

            // 根据 orgLeaders.sort 对专家按其最高职务排序
            const orderedFormerExpertList = leaderFormerList.sort((a, b) => {
                return getLeaderOrder(a) - getLeaderOrder(b);
            });
            setFormerLeaders(orderedFormerExpertList);
        }
    };

    // 查看专家
    const handleViewExpert = item => {
        navigate(`/dc/experts?detail=${item.id}`);
    };

    useEffect(() => {
        if (openDataOverviewDrawer && id) {
            dataCaseProjects();
            dataOrgMemberCount();
            dataExperts();
        }
    }, [openDataOverviewDrawer, id]);

    return (
        <>
            <Drawer
                closable={{ placement: 'end' }}
                className="organization-overview-drawer"
                title={
                    <div className="drawer-title">
                        <span>数据概览</span>
                        <span className="org-name">{currentOrg?.name}</span>
                        <Text type="secondary">#{id}</Text>
                        <Tag color="processing">医疗机构</Tag>
                    </div>
                }
                open={openDataOverviewDrawer}
                size="100%"
                placement="bottom"
                onClose={onClose}
                mask={{ closable: false }}
                footer={null}
            >
                <Row justify="center">
                    <Col md={24} lg={20} xl={18}>
                        <Form labelCol={{ sm: { span: 24 }, md: { span: 6 }, lg: { span: 4 } }} wrapperCol={{ sm: { span: 24 }, md: { span: 18 }, lg: { span: 20 } }}>
                            <div className="module-title">
                                <span>专家数据</span>
                                <Button type="link" onClick={viewExperts} className="org-experts-btn">
                                    查看该机构全部专家人员({currentOrg?.expertCount})
                                    <ChevronRight className="anticon" />
                                </Button>
                            </div>

                            {academicianList && !!academicianList.length && (
                                <Form.Item label="两院院士">
                                    <Row gutter={[16, 16]} className="academician-list">
                                        {academicianList.map((item, index) => {
                                            return (
                                                <Col span={12} key={`academician_${index}`}>
                                                    <div className="academician-item" onClick={() => handleViewExpert(item)}>
                                                        <div className="academician-info">
                                                            {!!item.expertAvatar ? <Avatar size={60} src={prepareLogoUrl(item.expertAvatar)} /> : <Avatar size={60} icon={<PersonFill />} />}

                                                            <div className="expert-info">
                                                                <div className="academician-name">
                                                                    {item.memUsername}
                                                                    {item.memGender === 1 && <PersonFill style={{ color: '#1890ff' }} />}
                                                                    {item.memGender === 0 && <PersonFill style={{ color: '#f759ab' }} />}
                                                                </div>
                                                                <div className="academician-title">{item.honoraryTitles}</div>
                                                            </div>
                                                        </div>

                                                        <ChevronRight />
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Form.Item>
                            )}

                            <Form.Item label="现任领导">
                                <Row gutter={[16, 16]} className="academician-list">
                                    {currentLeaders
                                        && !!currentLeaders.length
                                        && (showAllCurrentLeaders ? currentLeaders : currentLeaders.slice(0, 4)).map((item, index) => {
                                            return (
                                                <Col span={12} key={`current_leader_${index}`}>
                                                    <div className="academician-item" onClick={() => handleViewExpert(item)}>
                                                        <div className="academician-info">
                                                            {!!item.expertAvatar ? <Avatar size={60} src={prepareLogoUrl(item.expertAvatar)} /> : <Avatar size={60} icon={<PersonFill />} />}

                                                            <div className="expert-info">
                                                                <div className="academician-name">
                                                                    {item.memUsername}
                                                                    {item.memGender === 1 && <PersonFill style={{ color: '#1890ff' }} />}
                                                                    {item.memGender === 0 && <PersonFill style={{ color: '#f759ab' }} />}
                                                                </div>
                                                                {item.ssoMemberWorkExperiencesEntities && !!item.ssoMemberWorkExperiencesEntities.length && <div className="academician-title">{getLeaderTitle(item)}</div>}
                                                            </div>
                                                        </div>

                                                        <ChevronRight />
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                </Row>

                                {currentLeaders && currentLeaders.length > 4 && (
                                    <div className="show-more-btn">
                                        <Link onClick={() => setShowAllCurrentLeaders(!showAllCurrentLeaders)}>
                                            {showAllCurrentLeaders ? (
                                                <>
                                                    <CaretUp className="anticon" /> 收起
                                                </>
                                            ) : (
                                                <>
                                                    <CaretDown className="anticon" /> 展开全部{currentLeaders.length}位领导
                                                </>
                                            )}
                                        </Link>
                                    </div>
                                )}

                                {!currentLeaders.length && <Text type="secondary">暂无信息</Text>}
                            </Form.Item>

                            <Form.Item label="历任领导">
                                <Row gutter={[16, 16]} className="academician-list">
                                    {formerLeaders
                                        && !!formerLeaders.length
                                        && (showAllFormerLeaders ? formerLeaders : formerLeaders.slice(0, 4)).map((item, index) => {
                                            return (
                                                <Col span={12} key={`former_leader_${index}`}>
                                                    <div className="academician-item" onClick={() => handleViewExpert(item)}>
                                                        <div className="academician-info">
                                                            {!!item.expertAvatar ? <Avatar size={60} src={prepareLogoUrl(item.expertAvatar)} /> : <Avatar size={60} icon={<PersonFill />} />}

                                                            <div className="expert-info">
                                                                <div className="academician-name">
                                                                    {item.memUsername}
                                                                    {item.memGender === 1 && <PersonFill style={{ color: '#1890ff' }} />}
                                                                    {item.memGender === 0 && <PersonFill style={{ color: '#f759ab' }} />}
                                                                </div>
                                                                {item.ssoMemberWorkExperiencesEntities && !!item.ssoMemberWorkExperiencesEntities.length && <div className="academician-title">{getLeaderTitle(item)}</div>}
                                                            </div>
                                                        </div>

                                                        <ChevronRight />
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                </Row>

                                {formerLeaders && formerLeaders.length > 4 && (
                                    <div className="show-more-btn">
                                        <Link onClick={() => setShowAllFormerLeaders(!showAllFormerLeaders)}>
                                            {showAllFormerLeaders ? (
                                                <>
                                                    <CaretUp className="anticon" /> 收起
                                                </>
                                            ) : (
                                                <>
                                                    <CaretDown className="anticon" /> 展开全部{formerLeaders.length}位领导
                                                </>
                                            )}
                                        </Link>
                                    </div>
                                )}

                                {!formerLeaders.length && <Text type="secondary">暂无信息</Text>}
                            </Form.Item>

                            <div className="module-title">认证用户</div>

                            <Form.Item label="机构认证">{getAuthType(authCountInfo.memberDto)}</Form.Item>

                            <Form.Item label="个人专业认证">
                                {!!authCountInfo.authMemberCount && (
                                    <div className="auth-num">
                                        <span>累计认证人数</span>
                                        {authCountInfo.authMemberCount}
                                    </div>
                                )}
                                {!!authCountInfo.mdmMemberCount && (
                                    <div className="auth-num">
                                        <span>累计医疗决策人数</span>
                                        {authCountInfo.mdmMemberCount}
                                    </div>
                                )}
                                {!authCountInfo.mdmMemberCount && !authCountInfo.authMemberCount && <Text type="secondary">&ndash;</Text>}
                            </Form.Item>

                            <div className="module-title">实践征集</div>

                            <Table rowKey="id" columns={columns} size="small" dataSource={tableData} pagination={false} />
                        </Form>
                    </Col>
                </Row>
            </Drawer>
        </>
    );
};

export default DataOverviewDrawer;
